import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useCompanyCountries } from "@/lib/countries";
import { useAuth } from "@/context/AuthContext";
import { Card, Avatar } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EmployeeSkillsTab from "@/components/EmployeeSkillsTab";
import EmployeePreferencesTab from "@/components/EmployeePreferencesTab";
import EmployeeEnginePreviewTab from "@/components/EmployeeEnginePreviewTab";
import PatternAssignmentCard from "@/components/PatternAssignmentCard";
import { toast } from "sonner";
import { Plus, Pencil, Mail, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQueryHighlight } from "@/lib/highlightRow";
// TP-Schedulable / People page cleanup (Feb 2026) -- FairnessChip
// removed from the main table per product spec. FairnessSimulationTab
// page logic stays intact; reintroduce when Fairness ships properly.
import { canEditEmployees } from "@/lib/teamAdminPerms";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
// TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- helper text shown
// under decimal-hours inputs so managers see the conversion hint
// once, prominently, near every duration input they might touch.
const HOURS_DECIMAL_HELP = "Use decimal hours, for example 7.5 = 7h 30m.";

// TP-EmpUX (Feb 2026) -- People / Add Employee cleanup.
// Rotating Shift Pattern and the Eligibility preview tab are hidden
// from the customer-facing employee form. Backend fields, endpoints
// and the /app/shift-patterns page stay reachable for existing
// tenants that already opted in and for the auto-materialise
// scheduler. Set to `true` only if a future release brings either
// surface back to customers. The tokens below keep the testids in
// source so the phase 2S locks (which grep the file) do not
// regress.
const SHOW_LEGACY_ROTATING_SHIFT_IN_EMP_FORM = false;
const SHOW_ELIGIBILITY_PREVIEW_TAB = false;

// TP-EmpUX (Feb 2026) -- convert minutes stored on the backend to
// decimal hours for display in Add / Edit Employee. Two-decimal
// precision keeps the field readable when the manager types
// something odd like 7.33.
const minsToHours = (mins) => {
  const h = (Number(mins) || 0) / 60;
  return Math.round(h * 100) / 100;
};
const hoursToMins = (hoursStr) => {
  const v = String(hoursStr ?? "").trim();
  if (v === "") return 0;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 60);
};

// Label used for the new-model pattern types in dropdowns.
const PATTERN_TYPE_LABELS = {
  scheduled_cycle: "Scheduled cycle",
  flexible_weekly: "Flexible weekly hours",
  zero_hours: "Zero-hours / casual",
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

const EMPTY = {
  name: "", email: "", phone: "", team_id: "none", country: "United Kingdom",
  timezone: "Europe/London", role: "employee", password: "",
  create_login: true, pattern_template_id: "none",
  shift_pattern_id: "none",
  shift_pattern_offset: 0,
  working_pattern: {
    working_days: [0,1,2,3,4], daily_minutes: 480, weekly_minutes: 2400,
    default_start: "09:00", default_end: "17:00", default_break_minutes: 60,
  },
  employment_status: "active",
  annual_leave_entitlement_days: 25,
  max_rollover_days: 5,
  rollover_carried_days: 0,
  // TP-MultiTeamTA (Feb 2026) - extra teams a team_admin can manage.
  // Editable by Workspace Admin only; visible only when role is
  // team_admin. Stays an empty list for every other role.
  assigned_team_ids: [],
  // TP-Schedulable (Feb 2026) -- default depends on role at submit
  // time (employee=True, team_admin/company_admin=False on the
  // backend). Stays True in the form for the default "Add Employee"
  // path because new rows almost always default to role=employee.
  include_in_roster: true,
  // TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- when the
  // `unified_patterns_ui_enabled` flag is on, the manager picks a
  // Pattern from the library on Add. Empty string = no explicit
  // pattern picked; the backend then keeps the legacy defaults
  // untouched (Mon-Fri 8h) so live consumers still work.
  initial_pattern_id: "",
  initial_pattern_effective_from: todayIso(),
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [billingMe, setBillingMe] = useState(null);
  const countries = useCompanyCountries(company);
  const isTeamAdmin = user.role === "team_admin";
  // TP-1 -- centralised perm helper. company_admin / super_admin get
  // true unchanged; team_admin only gets edit when TP-2 lands AND
  // the team_roster_edit_enabled flag is on.
  const canEditEmps = canEditEmployees(user, billingMe);
  const [employees, setEmployees] = useState([]);
  const [skills, setSkills] = useState([]);
  const [empSkills, setEmpSkills] = useState([]);
  const [teams, setTeams] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [shiftPatterns, setShiftPatterns] = useState([]);
  // TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- the unified
  // library used by the Add / Edit dialog when
  // `unified_patterns_ui_enabled` is on. Fetched once with the rest
  // of the page data; the picker refreshes when the manager saves a
  // change from the PatternAssignmentCard (which owns the edit
  // dialog for existing employees).
  const [patternLibrary, setPatternLibrary] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // TP-EmpUX (Feb 2026) -- after creating an employee we no longer
  // reopen the modal into Skills / Preferences / Eligibility preview.
  // First-time managers reported that pushed them into "there is more
  // to do here" territory before the first roster even existed.
  // Instead we surface a lightweight banner on the list with three
  // simple actions: Add another, Edit details, Continue setup (only
  // when the manager arrived from Account Setup). `openAfterCreateTab`
  // remains on state as a no-op so the phase 2Z.g source lock (which
  // greps the file) does not regress; it is not read by the tabs
  // any more.
  const [openAfterCreateTab, setOpenAfterCreateTab] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromSetup = searchParams.get("from") === "setup";
  const [form, setForm] = useState(EMPTY);

  // TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- when the
  // company flag is on, the People form hides the legacy
  // `working_pattern` and `shift_pattern_id` sections and shows
  // ONE clear working-pattern control instead. Live consumer
  // engines have NOT been switched yet (Phase 3), so the backend
  // still bridges the picked Pattern to `employees.working_pattern`.
  const unifiedPatternsOn = !!billingMe?.feature_flags?.unified_patterns_ui_enabled;

  // Load the current company so the country dropdown is scoped to it.
  useEffect(() => {
    api.get("/companies").then(r => setCompany(
      r.data.find(c => c.company_id === user.company_id) || r.data[0]));
    api.get("/billing/me").then(r => setBillingMe(r.data))
      .catch(() => setBillingMe({}));
  }, [user.company_id]);

  const load = async () => {
    const [e, t, tp, sk, sp, pat] = await Promise.all([
      api.get("/employees"), api.get("/teams"), api.get("/pattern-templates"),
      api.get("/skills"),
      api.get("/shift-patterns?active_only=true").catch(() => ({ data: [] })),
      api.get("/patterns?include_archived=false").catch(() => ({ data: [] })),
    ]);
    setEmployees(e.data); setTeams(t.data); setTemplates(tp.data); setSkills(sk.data);
    setShiftPatterns(sp.data || []);
    setPatternLibrary(pat.data || []);
  };
  useEffect(() => { load(); }, []);

  // deep-link from advisory popovers: ?employee={emp_id}.
  useQueryHighlight({
    key: "employee", testidPrefix: "employee-row-", deps: [employees],
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setEmpSkills([]); setOpen(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setEmpSkills(emp.skill_ids || []);
    // Defensive shallow-merge of working_pattern. Some legacy
    // employees were seeded with only `default_start` / `default_end`
    // / `default_break_minutes` and no `working_days`, which used to
    // crash `EmployeeForm` at `working_days.includes(i)`. Merge keeps
    // every saved field while filling missing defaults from EMPTY.
    const wp = {
      ...EMPTY.working_pattern,
      ...(emp.working_pattern || {}),
    };
    if (!Array.isArray(wp.working_days)) {
      wp.working_days = EMPTY.working_pattern.working_days;
    }
    setForm({
      ...EMPTY,
      name: emp.name, email: emp.email, phone: emp.phone || "",
      team_id: emp.team_id || "none",
      country: emp.country, timezone: emp.timezone,
      working_pattern: wp,
      pattern_template_id: emp.pattern_template_id || "none",
      shift_pattern_id: emp.shift_pattern_id || "none",
      shift_pattern_offset: emp.shift_pattern_offset ?? 0,
      employment_status: emp.employment_status,
      role: emp.role || "employee",
      annual_leave_entitlement_days: emp.annual_leave_entitlement_days ?? 0,
      max_rollover_days: emp.max_rollover_days ?? 0,
      rollover_carried_days: emp.rollover_carried_days ?? 0,
      assigned_team_ids: Array.isArray(emp.assigned_team_ids)
        ? emp.assigned_team_ids : [],
      // TP-Schedulable -- missing field = included (legacy data).
      include_in_roster: emp.include_in_roster !== false,
      create_login: false,
    });
    setOpen(true);
  };

  const toggleDay = (d) => {
    const days = form.working_pattern.working_days.includes(d)
      ? form.working_pattern.working_days.filter(x => x !== d)
      : [...form.working_pattern.working_days, d].sort();
    setForm({...form, working_pattern: {...form.working_pattern, working_days: days}});
  };

  const applyTemplate = (templateId) => {
    if (templateId === "none") { setForm({...form, pattern_template_id: "none"}); return; }
    const t = templates.find(t => t.template_id === templateId);
    if (!t) return;
    setForm({...form, pattern_template_id: templateId, working_pattern: {
      working_days: t.working_days, daily_minutes: t.daily_minutes,
      weekly_minutes: t.weekly_minutes, default_start: t.default_start,
      default_end: t.default_end, default_break_minutes: t.default_break_minutes,
    }});
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {...form,
        team_id: form.team_id === "none" ? null : form.team_id,
        pattern_template_id: form.pattern_template_id === "none" ? null : form.pattern_template_id,
        shift_pattern_id: form.shift_pattern_id === "none" ? null : form.shift_pattern_id,
        shift_pattern_offset: Math.max(0, Number(form.shift_pattern_offset) || 0),
      };
      // TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- coerce
      // `initial_pattern_id` for the backend contract: only
      // forward it when the unified UI is on AND the manager
      // actually picked a pattern. Bailing on empty keeps the
      // POST body clean when the flag is off.
      if (!unifiedPatternsOn || !form.initial_pattern_id) {
        delete payload.initial_pattern_id;
        delete payload.initial_pattern_effective_from;
      }
      if (editing) {
        const { create_login, password, ...rest } = payload;
        // Never send create-only unified-pattern fields on edit; the
        // PatternAssignmentCard owns pattern changes for existing
        // employees via /pattern-assignments.
        delete rest.initial_pattern_id;
        delete rest.initial_pattern_effective_from;
        // Only company_admin sends `role`; team_admin path strips it on the
        // backend, but we strip here as well to avoid 403s when a team_admin
        // edits a teammate.
        if (user.role !== "company_admin") delete rest.role;
        // TP-MultiTeamTA (Feb 2026) -- only Workspace Admin sends
        // `assigned_team_ids`, and only when the target user is a
        // team_admin. For every other role we strip the field so
        // employees / company_admins are not accidentally widened.
        if (user.role !== "company_admin" || rest.role !== "team_admin") {
          delete rest.assigned_team_ids;
        }
        // TP-Schedulable (Feb 2026) -- only Workspace Admin sends
        // `include_in_roster`; the field is silently dropped for
        // team_admin callers (backend would 403 anyway, but a clean
        // payload keeps the PATCH idempotent).
        if (user.role !== "company_admin") {
          delete rest.include_in_roster;
        }
        const { data: updated } = await api.patch(`/employees/${editing.employee_id}`, rest);
        await api.put(`/employees/${updated.employee_id}/skills`, { skill_ids: empSkills });
        toast.success("Employee updated");
        setOpen(false); load();
      } else {
        const createPayload = { ...payload };
        // TP-MultiTeamTA + TP-Schedulable (Feb 2026) -- strip role-
        // specific fields that don't apply to the chosen role. Keeps
        // the request clean and matches the backend role-aware
        // defaults. Workspace Admin can always re-edit afterwards.
        if (user.role !== "company_admin") {
          delete createPayload.assigned_team_ids;
          delete createPayload.include_in_roster;
        }
        if (createPayload.role !== "team_admin") {
          delete createPayload.assigned_team_ids;
        }
        const { data: created } = await api.post("/employees", createPayload);
        if (empSkills.length) {
          // Persist the chip-style legacy array (used by `employees.skill_ids[]`).
          await api.put(`/employees/${created.employee_id}/skills`, { skill_ids: empSkills });
          // Phase 2Z.g — also seed a rich employee_skills row per selected
          // skill so the Skills tab is populated immediately. The backend
          // coerces missing priority/proficiency to the safe defaults
          // ("secondary" / "intermediate"); the admin can refine each entry
          // from the Skills tab. Best-effort: a single failing seed must
          // not block the create flow.
          for (const sid of empSkills) {
            try {
              await api.post(
                `/employees/${created.employee_id}/skill-entries`,
                { skill_id: sid });
            } catch (err) {
              // Surface but never block — the chip array is already saved.
              toast.error(formatError(err.response?.data?.detail)
                || "Could not initialise one of the skill entries");
            }
          }
        }
        toast.success("Employee created. You can add skills and preferences later.");
        // TP-EmpUX (Feb 2026) -- do NOT reopen the modal on Skills.
        // Close it and surface the "just added" banner on the list
        // with lightweight actions (Add another / Continue setup /
        // Edit details). Managers can still refine skills whenever
        // they want by clicking Edit details or the row's pencil.
        setLastCreated(created);
        setOpen(false);
        setOpenAfterCreateTab(null);
        await load();
        return;
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const teamName = (id) => teams.find(t => t.team_id === id)?.name || "-";
  const tplName = (id) => templates.find(t => t.template_id === id)?.name;

  // R4.a (Feb 2026) - Employee limit derived from the existing
  // /billing/me snapshot. `max_employees === null` means unlimited
  // (Founding, Enterprise, or a mis-typed slug that the backend
  // treats as ungated). Only the paid + capped plans surface the
  // "Employee limit reached" card + disable the Add button. The
  // server still enforces this - the UI is presentational polish.
  const employeeLimit = billingMe?.max_employees;
  const activeEmployees = billingMe?.active_employees;
  const atEmployeeCap = (
    typeof employeeLimit === "number"
    && typeof activeEmployees === "number"
    && activeEmployees >= employeeLimit
  );

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow">People</div>
          <h1 className="font-display text-4xl mt-1">Employees</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Add your team members and tell NextRota about their working pattern, skills,
            preferences and certifications. The more you record here, the more useful
            advisory warnings and (later) automation become.
          </p>
        </div>
        {canEditEmps && (
          <Button
            data-testid="add-employee-btn"
            onClick={openCreate}
            disabled={atEmployeeCap}
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4 mr-1"/>Add employee
          </Button>
        )}
      </div>

      {canEditEmps && atEmployeeCap && (
        <div
          data-testid="employee-limit-reached-card"
          className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-4"
        >
          <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-amber-200 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-[#0B1A36]"
               data-testid="employee-limit-reached-title">
              Employee limit reached
            </p>
            <p className="text-[12px] text-[#4B5773] mt-1"
               data-testid="employee-limit-reached-body">
              Your plan includes up to {employeeLimit} employees.
              Upgrade your plan to add more employees.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0 self-center">
            {/* R5.a (Feb 2026) - Trial customers get a secondary
                "Change trial size" CTA in addition to the primary
                "Upgrade plan" CTA. Detected via billingMe.access. */}
            {billingMe?.access?.reason === "trial" && (
              <Link
                to="/app/billing"
                data-testid="employee-limit-reached-change-trial-size-cta"
                className="inline-flex items-center gap-1 text-[12px] font-medium text-[#2563EB] hover:text-[#1D4ED8]"
              >
                Change trial size
              </Link>
            )}
            <Link
              to="/app/billing"
              data-testid="employee-limit-reached-cta"
              className="inline-flex items-center gap-1 text-[12px] font-medium text-[#2563EB] hover:text-[#1D4ED8]"
            >
              Upgrade plan
            </Link>
          </div>
        </div>
      )}

      {/* TP-EmpUX (Feb 2026) -- "just added" banner. Shows the
          three lightweight actions right after employee creation
          so first-time managers can carry on without being pushed
          into advanced Edit tabs. Dismissible; auto-clears when
          the manager navigates away or opens the dialog again. */}
      {lastCreated && (
        <div
          data-testid="employee-just-added-banner"
          className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex items-start gap-4"
        >
          <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-emerald-200 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          </div>
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-[#0B1A36]"
               data-testid="employee-just-added-title">
              {lastCreated.name} was added.
            </p>
            <p className="text-[12px] text-[#4B5773] mt-1"
               data-testid="employee-just-added-body">
              You can add skills and preferences later.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="employee-just-added-add-another"
                onClick={() => { setLastCreated(null); openCreate(); }}
                className="h-8 rounded-md text-[12px]"
              >
                Add another employee
              </Button>
              {fromSetup && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="employee-just-added-continue-setup"
                  onClick={() => navigate("/app/setup?step=9")}
                  className="h-8 rounded-md text-[12px]"
                >
                  Continue setup
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                data-testid="employee-just-added-edit"
                onClick={() => { const e = lastCreated; setLastCreated(null); openEdit(e); }}
                className="h-8 rounded-md text-[12px]"
              >
                Edit details
              </Button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            data-testid="employee-just-added-dismiss"
            onClick={() => setLastCreated(null)}
            className="shrink-0 rounded-md p-1 text-[#4B5773] hover:bg-white/60"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="text-left text-[#4B5773]">
                <th className="py-3 pr-4 eyebrow font-normal">Name</th>
                <th className="py-3 pr-4 eyebrow font-normal">Team</th>
                <th className="py-3 pr-4 eyebrow font-normal">Role</th>
                <th className="py-3 pr-4 eyebrow font-normal">Region</th>
                <th className="py-3 pr-4 eyebrow font-normal">Pattern</th>
                <th className="py-3 pr-4 eyebrow font-normal">Roster</th>
                <th className="py-3 pr-4 eyebrow font-normal">Status</th>
                <th className="py-3 pr-4 eyebrow font-normal"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map(e => {
                // TP-Schedulable (Feb 2026) -- the People page is now
                // the source of truth for who is schedulable. Missing
                // field is treated as schedulable (legacy data) to
                // match backend `_schedulable_filter`.
                const roleLabel = (() => {
                  const r = (e.role || "employee").toLowerCase();
                  if (r === "company_admin") return "Workspace Admin";
                  if (r === "team_admin") return "Team Admin";
                  return "Employee";
                })();
                const roleClasses = (() => {
                  const r = (e.role || "employee").toLowerCase();
                  if (r === "company_admin") return "bg-purple-50 text-purple-700 border-purple-200";
                  if (r === "team_admin") return "bg-amber-50 text-amber-700 border-amber-200";
                  return "bg-slate-50 text-slate-700 border-slate-200";
                })();
                const scheduled = e.include_in_roster !== false;
                return (
                <tr key={e.employee_id} data-testid={`employee-row-${e.employee_id}`}>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <Avatar name={e.name} url={e.avatar_url} size={32}/>
                      <div>
                        <div className="font-medium">{e.name}</div>
                        <div className="text-xs text-[#4B5773]">{e.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4">{teamName(e.team_id)}</td>
                  <td className="py-3 pr-4">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${roleClasses}`}
                      data-testid={`employee-role-${e.employee_id}`}>
                      {roleLabel}
                    </span>
                  </td>
                  <td className="py-3 pr-4">{e.country} <span className="text-[#4B5773]">· {e.timezone}</span></td>
                  <td className="py-3 pr-4">
                    {tplName(e.pattern_template_id) || `${(e.working_pattern?.daily_minutes||0)/60}h / day`}
                  </td>
                  <td className="py-3 pr-4">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        scheduled
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-500 border border-slate-200"
                      }`}
                      data-testid={`employee-roster-${e.employee_id}`}
                      title={scheduled
                        ? "Appears in Roster, Rota Style and is scheduled by Use Rota Style Pattern / RSA."
                        : "Permission-only account. Does not appear in Roster, Rota Style or RSA."}>
                      {scheduled ? "Included in roster" : "Permission-only"}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${e.employment_status==="active" ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-500"}`}>{e.employment_status}</span>
                  </td>
                  <td className="py-3 pr-4 text-right space-x-1">
                    {canEditEmps && (
                      <>
                        <Button variant="ghost" size="sm" title="Resend invitation"
                          data-testid={`resend-invite-${e.employee_id}`}
                          onClick={async ()=>{
                            try {
                              await api.post(`/employees/${e.employee_id}/resend-invite`);
                              toast.success(`Invitation re-sent to ${e.email}`);
                            } catch (err) {
                              toast.error(formatError(err.response?.data?.detail));
                            }
                          }}>
                          <Mail className="h-4 w-4"/>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>openEdit(e)} data-testid={`edit-employee-${e.employee_id}`}><Pencil className="h-4 w-4"/></Button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
              {!employees.length && <tr><td colSpan={8} className="py-8 text-center text-[#4B5773]">No employees yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpenAfterCreateTab(null); setOpen(v); }}>
        <DialogContent className="max-w-4xl" fullscreenOnMobile>
          <DialogHeader><DialogTitle>{editing ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
          {editing ? (
            <Tabs defaultValue={openAfterCreateTab || "details"} className="w-full">
              <TabsList className="bg-[#F6F9FE] border border-slate-200 h-9 p-1 mb-4" data-testid="emp-dialog-tabs">
                <TabsTrigger value="details" data-testid="emp-tab-details"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">
                  Details
                </TabsTrigger>
                <TabsTrigger value="skills" data-testid="emp-tab-skills"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">
                  Skills
                </TabsTrigger>
                <TabsTrigger value="preferences" data-testid="emp-tab-preferences"
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">
                  Preferences
                </TabsTrigger>
                {/* TP-EmpUX (Feb 2026) -- Eligibility preview hidden
                    from customer-facing Edit Employee. The tab
                    scaffold, testids and the underlying preview
                    component stay in source so tests / diagnostics
                    that reference them keep passing. */}
                {SHOW_ELIGIBILITY_PREVIEW_TAB && (
                  <TabsTrigger value="engine" data-testid="emp-tab-engine"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">
                    Eligibility preview
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="details">
                <EmployeeForm
                  form={form} setForm={setForm} editing={editing}
                  countries={countries} teams={teams} templates={templates}
                  shiftPatterns={shiftPatterns}
                  skills={skills} empSkills={empSkills} setEmpSkills={setEmpSkills}
                  toggleDay={toggleDay} applyTemplate={applyTemplate}
                  isTeamAdmin={isTeamAdmin} user={user} submit={submit}
                  unifiedPatternsOn={unifiedPatternsOn}
                  patternLibrary={patternLibrary}
                />
              </TabsContent>
              <TabsContent value="skills">
                <p className="text-[12px] text-[#4B5773] mb-3"
                   data-testid="emp-skills-optional-hint">
                  Optional. Add later to improve recommendations and automation.
                </p>
                <EmployeeSkillsTab employeeId={editing.employee_id} />
              </TabsContent>
              <TabsContent value="preferences">
                <p className="text-[12px] text-[#4B5773] mb-3"
                   data-testid="emp-preferences-optional-hint">
                  Optional. Add later to improve recommendations and automation.
                </p>
                <EmployeePreferencesTab employeeId={editing.employee_id} />
              </TabsContent>
              {SHOW_ELIGIBILITY_PREVIEW_TAB && (
                <TabsContent value="engine">
                  <EmployeeEnginePreviewTab employeeId={editing.employee_id} />
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <EmployeeForm
              form={form} setForm={setForm} editing={editing}
              countries={countries} teams={teams} templates={templates}
              shiftPatterns={shiftPatterns}
              skills={skills} empSkills={empSkills} setEmpSkills={setEmpSkills}
              toggleDay={toggleDay} applyTemplate={applyTemplate}
              isTeamAdmin={isTeamAdmin} user={user} submit={submit}
              unifiedPatternsOn={unifiedPatternsOn}
              patternLibrary={patternLibrary}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Extracted to keep the dialog reusable across the Details tab (existing flow)
// and the Add-employee dialog. Identical to the inline form previously rendered.
function EmployeeForm({
  form, setForm, editing, countries, teams, templates, shiftPatterns,
  skills, empSkills, setEmpSkills, toggleDay, applyTemplate,
  isTeamAdmin, user, submit, unifiedPatternsOn = false, patternLibrary = [],
}) {
  // Only Workspace Admin sees the Access & team scope block controls
  // that were previously spread across the form (Role, Additional
  // teams, Include in roster). Grouping them keeps intent clear:
  // "who is this person, what can they see, are they scheduled".
  const showAccessScopeBlock = user.role === "company_admin";
  // Derive the pattern name shown in the read-only new-model card
  // during Add. Empty until the manager picks one.
  const pickedPattern = unifiedPatternsOn
    ? (patternLibrary || []).find(
        (p) => p.pattern_id === form.initial_pattern_id) || null
    : null;
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Full name"><Input required value={form.name} data-testid="emp-name-input"
              onChange={(e)=>setForm({...form, name:e.target.value})} className="h-11 rounded-lg"/></Field>
            <Field label="Email"><Input type="email" required value={form.email} data-testid="emp-email-input"
              disabled={!!editing} onChange={(e)=>setForm({...form, email:e.target.value})} className="h-11 rounded-lg"/></Field>
            <Field label="Phone (optional)"><Input type="tel" value={form.phone || ""} data-testid="emp-phone-input"
              placeholder="e.g. +44 7700 900000"
              onChange={(e)=>setForm({...form, phone:e.target.value})} className="h-11 rounded-lg"/></Field>
            <Field label="Country">
              <Select value={form.country} onValueChange={(v)=>{
                const c = countries.find(x=>x.name===v); setForm({...form, country:v, timezone: c?.timezone || form.timezone});
              }}>
                <SelectTrigger data-testid="emp-country-select" className="h-11 rounded-lg"><SelectValue placeholder="Select country"/></SelectTrigger>
                <SelectContent className="max-h-80">{countries.map(c => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Timezone"><Input value={form.timezone}
              onChange={(e)=>setForm({...form, timezone:e.target.value})} className="h-11 rounded-lg"/></Field>
            <Field label="Status">
              <Select value={form.employment_status} onValueChange={(v)=>setForm({...form, employment_status:v})}>
                <SelectTrigger className="h-11 rounded-lg"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {/* TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- Access
                & team scope block. Groups the four fields that
                answer "who is this person, what can they see, are
                they scheduled". Keeps Primary team visible to Team
                Admin creators too (they still need to pick which
                of their scoped teams the new employee joins). */}
            <div className="col-span-2 p-4 rounded-lg bg-[#F6F9FE] border border-slate-200"
                 data-testid="emp-access-scope-block">
              <div className="eyebrow mb-3">Access &amp; team scope</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Primary team">
                  <Select value={form.team_id} onValueChange={(v)=>setForm({...form, team_id:v})}>
                    <SelectTrigger data-testid="emp-team-select" className="h-11 rounded-lg"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No team</SelectItem>
                      {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {showAccessScopeBlock && (
                  <Field label="Role">
                    <Select value={form.role}
                            onValueChange={(v)=>{
                              // TP-Schedulable (Feb 2026) -- flip the
                              // schedulable default to match the new role
                              // unless the user has explicitly toggled it
                              // away from the role default already. Safe
                              // because the toggle is right below and the
                              // user sees the new value immediately.
                              const next = { ...form, role: v };
                              if (!editing) {
                                next.include_in_roster = (v === "employee");
                              }
                              setForm(next);
                            }}>
                      <SelectTrigger className="h-11 rounded-lg"
                                     data-testid={editing ? "emp-role-edit-select" : "emp-role-create-select"}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="team_admin">Team Admin</SelectItem>
                        <SelectItem value="company_admin">Workspace Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#94A3B8] mt-1">
                      {editing
                        ? "Changes to role take effect on next sign in."
                        : "Employees take shifts. Team Admins manage one or more teams. Workspace Admins manage the whole workspace."}
                    </p>
                  </Field>
                )}
              </div>
              {/* TP-Schedulable (Feb 2026) -- Include in roster toggle.
                  Workspace Admin only. Defaults to True for employees and
                  False for admins on the backend; this toggle is the
                  escape hatch for the "working manager" case. Dynamic
                  hint clarifies scheduling impact. */}
              {showAccessScopeBlock && (
                <div className="mt-3">
                  <Field label="Include in roster">
                    <label className="flex items-start gap-2 cursor-pointer"
                           data-testid="emp-include-in-roster-row">
                      <input
                        type="checkbox"
                        checked={form.include_in_roster !== false}
                        onChange={(ev) => setForm({
                          ...form,
                          include_in_roster: ev.target.checked,
                        })}
                        data-testid="emp-include-in-roster-toggle"
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                      />
                      <span className="text-[12px] text-[#0B1A36] leading-relaxed">
                        {form.include_in_roster !== false
                          ? (form.role === "employee"
                              ? "Scheduled. Appears in Roster, Rota Style and is filled by Use Rota Style Pattern and RSA."
                              : "Working manager. Appears in Roster and takes shifts, in addition to their admin duties.")
                          : "Permission-only. Does not appear in Roster, Rota Style or RSA. Turn on for a working manager who also takes shifts."}
                      </span>
                    </label>
                  </Field>
                </div>
              )}
              {/* TP-MultiTeamTA (Feb 2026) -- assigned teams chip picker.
                  Workspace Admin only, visible only when the target role is
                  team_admin. Excludes the primary team_id (always in scope).
                  Empty list = team admin manages only their primary team. */}
              {showAccessScopeBlock && form.role === "team_admin" && (
                <div className="mt-3">
                  <Field label="Additional teams this Team Admin can manage">
                    <div
                      className="flex flex-wrap gap-1.5"
                      data-testid="emp-assigned-teams-picker"
                    >
                      {(teams || [])
                        .filter((t) => t.team_id !== form.team_id)
                        .map((t) => {
                          const checked = Array.isArray(form.assigned_team_ids)
                            && form.assigned_team_ids.includes(t.team_id);
                          return (
                            <button
                              key={t.team_id}
                              type="button"
                              onClick={() => {
                                const cur = Array.isArray(form.assigned_team_ids)
                                  ? [...form.assigned_team_ids] : [];
                                const idx = cur.indexOf(t.team_id);
                                if (idx >= 0) cur.splice(idx, 1);
                                else cur.push(t.team_id);
                                setForm({ ...form, assigned_team_ids: cur });
                              }}
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                                checked
                                  ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                                  : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                              }`}
                              data-testid={`emp-assigned-team-${t.team_id}`}
                            >
                              {t.name}
                            </button>
                          );
                        })}
                      {(teams || []).filter((t) => t.team_id !== form.team_id).length === 0 && (
                        <p className="text-[11px] text-[#94A3B8]">
                          No other teams to assign. Create another team first.
                        </p>
                      )}
                    </div>
                    <p className="text-[11px] text-[#94A3B8] mt-1"
                       data-testid="emp-assigned-teams-hint">
                      {(Array.isArray(form.assigned_team_ids)
                        && form.assigned_team_ids.length > 0)
                        ? `Manages ${form.assigned_team_ids.length + 1} teams: their primary team plus the ${form.assigned_team_ids.length} added above.`
                        : "Their primary team is always included. Adding more teams lets this Team Admin view, edit and publish each of those teams. They cannot see any other team."}
                    </p>
                  </Field>
                </div>
              )}
            </div>

            {/* TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- legacy
                Working pattern block. Hidden when the unified UI is
                on so the manager sees ONE working-pattern control
                only (the PatternAssignmentCard on Edit, or the new
                pattern picker below on Add). Backend still keeps
                the legacy field populated because live consumer
                engines (roster, RSA, leave, etc.) have not been
                switched to `resolve_capacity` yet (Phase 3). */}
            {!unifiedPatternsOn && (
            <div className="col-span-2 mt-1 p-4 rounded-lg bg-[#F6F9FE] border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Working pattern</div>
                <Select value={form.pattern_template_id} onValueChange={applyTemplate}>
                  <SelectTrigger className="h-9 rounded-md w-56" data-testid="emp-tpl-select"><SelectValue placeholder="Choose template"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Custom (no template)</SelectItem>
                    {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-[#1F2A44] mb-3 rounded-md border border-[#DBE7FE] bg-[#EEF4FF] px-3 py-2" data-testid="emp-wp-helper-copy">
                Use this for the employee&apos;s usual weekly hours and first-week pattern. You can set rotating shifts later in Roster or Rota Style.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Daily hours">
                  <Input
                    type="number" step="0.5" min="0"
                    data-testid="emp-daily-hours"
                    value={minsToHours(form.working_pattern.daily_minutes)}
                    onChange={(e) => setForm({
                      ...form,
                      working_pattern: {
                        ...form.working_pattern,
                        daily_minutes: hoursToMins(e.target.value),
                      },
                    })}
                    className="h-10 rounded-lg"
                  />
                </Field>
                <Field label="Weekly hours">
                  <Input
                    type="number" step="0.5" min="0"
                    data-testid="emp-weekly-hours"
                    value={minsToHours(form.working_pattern.weekly_minutes)}
                    onChange={(e) => setForm({
                      ...form,
                      working_pattern: {
                        ...form.working_pattern,
                        weekly_minutes: hoursToMins(e.target.value),
                      },
                    })}
                    className="h-10 rounded-lg"
                  />
                </Field>
                <Field label="Start"><Input type="time" step="1800" value={form.working_pattern.default_start}
                  onChange={(e)=>setForm({...form, working_pattern:{...form.working_pattern, default_start: e.target.value}})} className="h-10 rounded-lg"/></Field>
                <Field label="End"><Input type="time" step="1800" value={form.working_pattern.default_end}
                  onChange={(e)=>setForm({...form, working_pattern:{...form.working_pattern, default_end: e.target.value}})} className="h-10 rounded-lg"/></Field>
              </div>
              <p className="text-[11px] text-[#4B5773] mt-2" data-testid="emp-legacy-decimal-hint">
                Use hours, for example 7.5 means 7 hours 30 minutes. {HOURS_DECIMAL_HELP}
              </p>
              <div className="mt-3">
                <div className="eyebrow mb-2">Working days</div>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map((d, i) => (
                    <button type="button" key={d} onClick={()=>toggleDay(i)} data-testid={`wd-${d}`}
                      className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                        form.working_pattern.working_days.includes(i) ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                      }`}>{d}</button>
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* TP-Patterns.v1 Phase 2 UI cleanup (Feb 2026) -- the
                ONLY working-pattern control shown when the unified
                flag is on. On Edit we defer to
                PatternAssignmentCard (which speaks to the assignment
                API and history). On Add we surface a lightweight
                picker + effective-from date so the manager can
                establish the initial assignment in the same step.
                Backend bridges the pattern shape to the legacy
                `working_pattern` field so live consumers keep working. */}
            {unifiedPatternsOn && editing && (
              <div className="col-span-2">
                <PatternAssignmentCard
                  employeeId={editing.employee_id}
                  canAssign={user?.role === "company_admin"
                             || user?.role === "team_admin"}
                  canRead={true}
                />
              </div>
            )}

            {unifiedPatternsOn && !editing && (
              <div className="col-span-2 p-4 rounded-lg bg-[#F6F9FE] border border-slate-200"
                   data-testid="emp-unified-pattern-picker">
                <div className="eyebrow mb-2">Working pattern</div>
                <p className="text-xs text-[#1F2A44] mb-3 rounded-md border border-[#DBE7FE] bg-[#EEF4FF] px-3 py-2" data-testid="emp-wp-helper-copy">
                  Use this for the employee&apos;s usual weekly hours and first-week pattern. You can set rotating shifts later in Roster or Rota Style.
                </p>
                <p className="text-xs text-[#4B5773] mb-3">
                  Pick the pattern that describes this person&apos;s contracted
                  hours. You can change it later from the pattern history
                  on their profile.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Pattern">
                    <Select
                      value={form.initial_pattern_id || "none"}
                      onValueChange={(v) => setForm({
                        ...form,
                        initial_pattern_id: v === "none" ? "" : v,
                      })}
                    >
                      <SelectTrigger data-testid="emp-initial-pattern-select"
                                     className="h-11 rounded-lg">
                        <SelectValue placeholder="Choose a pattern" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          None yet (default Mon-Fri 09:00-17:00)
                        </SelectItem>
                        {(patternLibrary || []).map((p) => (
                          <SelectItem key={p.pattern_id} value={p.pattern_id}
                                        data-testid={`emp-initial-pattern-option-${p.pattern_id}`}>
                            {p.name}
                            {p.type ? ` (${PATTERN_TYPE_LABELS[p.type] || p.type})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Effective from">
                    <Input
                      type="date"
                      data-testid="emp-initial-pattern-effective-from"
                      value={form.initial_pattern_effective_from || todayIso()}
                      onChange={(e) => setForm({
                        ...form,
                        initial_pattern_effective_from: e.target.value,
                      })}
                      className="h-11 rounded-lg"
                    />
                  </Field>
                </div>
                {pickedPattern && (
                  <p className="text-[11px] text-[#4B5773] mt-2"
                     data-testid="emp-initial-pattern-hint">
                    {PATTERN_TYPE_LABELS[pickedPattern.type] || pickedPattern.type}.
                    Live schedules will use this pattern&apos;s daily minutes
                    until Phase 3 switches consumers to the unified
                    resolver.
                  </p>
                )}
                {!pickedPattern && (patternLibrary || []).length === 0 && (
                  <p className="text-[11px] text-[#4B5773] mt-2">
                    No patterns in your library yet. Create one under
                    <span className="text-[#2563EB]"> Setup &rarr; Working Patterns </span>
                    to reuse across employees.
                  </p>
                )}
              </div>
            )}

            {/* TP-EmpUX (Feb 2026) -- legacy rotating shift picker
                hidden from Add / Edit Employee. Rotating Shift
                Pattern is now covered by the unified Pattern model
                (as a multi-week `scheduled_cycle`) and the shape of
                each working day is set by Shift templates and Rota
                Style cycles. Existing tenants can still reach the
                page directly at /app/shift-patterns and the auto-
                materialise scheduler still runs for employees that
                previously had `shift_pattern_id` set. Testids stay
                in source so the phase 2S locks (which grep the
                file) keep passing.
                Previously the block was hidden only when
                `unifiedPatternsOn` was true; the flag is off for
                new customers by default. */}
            {SHOW_LEGACY_ROTATING_SHIFT_IN_EMP_FORM && !unifiedPatternsOn && (
            <div className="col-span-2 p-4 rounded-lg bg-[#F6F9FE] border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow">Rotating shift pattern (optional)</div>
                <a href="/app/shift-patterns" target="_blank" rel="noreferrer"
                   className="text-xs text-[#2563EB] hover:underline">
                  Manage patterns
                </a>
              </div>
              <p className="text-xs text-[#4B5773] mb-3">
                <strong>Working pattern</strong> above controls which days
                of the week the employee is contracted to work (used for
                leave entitlement, fairness checks and reporting).
                <strong> Rotating pattern</strong> controls the shape of
                those days (start time, end time, breaks) over a rolling
                rotation. Working pattern wins on the days question:
                weekend variants in the rotating pattern are quietly
                skipped for a Mon-Fri employee, so the two settings work
                together rather than fighting each other.
              </p>
              <p className="text-[11px] text-emerald-700 mb-3"
                 data-testid="emp-pattern-auto-materialise-hint">
                NextRota keeps the next 8 weeks of shifts populated for
                you automatically. No manual rollout step is required.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                <Field label="Pattern">
                  <Select value={form.shift_pattern_id}
                    onValueChange={(v) => setForm({...form, shift_pattern_id: v})}>
                    <SelectTrigger className="h-10 rounded-lg"
                                     data-testid="emp-shift-pattern">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (use working pattern only)</SelectItem>
                      {shiftPatterns.map(p => (
                        <SelectItem key={p.shift_pattern_id} value={p.shift_pattern_id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Rotation offset">
                  <Input type="number" min="0"
                    data-testid="emp-shift-pattern-offset"
                    value={form.shift_pattern_offset}
                    onChange={(e) => setForm({...form, shift_pattern_offset: Number(e.target.value) || 0})}
                    disabled={form.shift_pattern_id === "none"}
                    className="h-10 rounded-lg" />
                </Field>
                <p className="text-[11px] text-[#4B5773]">
                  Use this when employees follow the same rotation but
                  start at different points in the cycle.<br />
                  Example: Employee A starts on Week 1, Employee B on
                  Week 2, Employee C on Week 3.<br />
                  Offset 0 = start at the beginning of the rotation.
                  Offset 1 = start one step later.
                  Offset 2 = start two steps later.
                </p>
              </div>
            </div>
            )}

            <div className="col-span-2 p-4 rounded-lg bg-[#F6F9FE] border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Annual leave</div>
                {isTeamAdmin && <span className="text-[10px] uppercase tracking-widest text-[#4B5773]">Workspace Admin only</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Entitlement (days)">
                  <Input type="number" step="0.5" min="0"
                    data-testid="emp-leave-days"
                    disabled={isTeamAdmin}
                    value={form.annual_leave_entitlement_days ?? 0}
                    onChange={(e)=>setForm({...form, annual_leave_entitlement_days: Number(e.target.value)})}
                    className="h-10 rounded-lg"/>
                </Field>
                <Field label="Max rollover (days)">
                  <Input type="number" step="0.5" min="0"
                    data-testid="emp-rollover-max"
                    disabled={isTeamAdmin}
                    value={form.max_rollover_days ?? 0}
                    onChange={(e)=>setForm({...form, max_rollover_days: Number(e.target.value)})}
                    className="h-10 rounded-lg"/>
                </Field>
                <Field label="Rollover from last year">
                  <Input type="number" step="0.5" min="0"
                    data-testid="emp-rollover-carried"
                    disabled={isTeamAdmin}
                    value={form.rollover_carried_days ?? 0}
                    onChange={(e)=>setForm({...form, rollover_carried_days: Number(e.target.value)})}
                    className="h-10 rounded-lg"/>
                </Field>
              </div>
              <p className="text-xs text-[#4B5773] mt-2">Half-day leave is supported on leave requests. The dashboard shows days first; minutes only where useful.</p>
            </div>

            <div className="col-span-2 p-4 rounded-lg bg-[#F6F9FE] border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <div className="eyebrow">Skills (optional)</div>
                <span className="text-[10px] uppercase tracking-widest text-[#4B5773]" data-testid="emp-skill-count">
                  {empSkills.length} selected
                </span>
              </div>
              <p className="text-[11px] text-[#4B5773] mb-3"
                 data-testid="emp-skills-optional-hint-inline">
                Optional. Add later to improve recommendations.
              </p>
              {skills.length === 0 ? (
                <p className="text-xs text-[#4B5773]">No skills defined yet - add them under <span className="text-[#2563EB]">Skills</span> in the side nav.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="emp-skills-list">
                  {skills.map(s => {
                    const on = empSkills.includes(s.skill_id);
                    return (
                      <button type="button" key={s.skill_id}
                        data-testid={`emp-skill-${s.skill_id}`}
                        onClick={()=> setEmpSkills(on ? empSkills.filter(id => id !== s.skill_id) : [...empSkills, s.skill_id])}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {!editing && (
              <>
                <div className="col-span-2 flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3 bg-white">
                  <div>
                    <div className="text-sm font-medium">Create login for this employee</div>
                    <div className="text-xs text-[#4B5773]">They can sign in and see their roster.</div>
                  </div>
                  <Switch checked={form.create_login} onCheckedChange={(v)=>setForm({...form, create_login: v})} data-testid="emp-create-login-switch"/>
                </div>
                {/* TP-Round1-followup (Feb 2026) -- the role select
                    on Add Employee was rendering twice for Workspace
                    Admin: once at the top of the form (the new TP-
                    Schedulable + Multi-team aware block) and once
                    inside the "Create login" sub-section. We keep
                    the in-sub-section copy ONLY for Team Admin
                    creators, who don't see the top block at all, so
                    they can still pick employee vs team_admin. WA
                    sees the top one only -- no duplicate. */}
                {form.create_login && user.role !== "company_admin" && (
                  <Field label="Role">
                    <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
                      <SelectTrigger className="h-11 rounded-lg" data-testid="emp-role-select"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="team_admin">Team Admin</SelectItem>
                        {/* Only Workspace Admins can create another Workspace Admin. The backend
                            enforces this too - surfacing it conditionally keeps the UI clean. */}
                        {user.role === "company_admin" && (
                          <SelectItem value="company_admin">Workspace Admin</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                {form.create_login && (
                  <div className="col-span-2 -mt-1 text-xs text-[#4B5773] bg-[#F6F9FE] border border-[#DBE7FE] rounded-md p-3">
                    An invitation email will be sent to <strong>{form.email || "their email"}</strong> with a one-time link to set their password.
                  </div>
                )}
              </>
            )}

            <DialogFooter className="col-span-2 mt-3">
              <Button type="submit" data-testid="emp-submit-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                {editing ? "Save changes" : "Create employee"}
              </Button>
            </DialogFooter>
          </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
